import { useEffect, useRef } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * BackgroundAutomationRunner - Auto-Processing Mode
 * 
 * This component automatically triggers job processing every 60 seconds
 * while the user has any browser tab open. This works around the cron job
 * permission issues in Supabase.
 * 
 * Processing happens as long as:
 * 1. User is logged in
 * 2. There are running jobs
 * 3. Browser tab is open (any tab in the app)
 */
export const BackgroundAutomationRunner = () => {
  const { user } = useSecureAuthContext();
  const intervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      console.log('🤖 Auto-Processor: No user logged in');
      return;
    }

    console.log('🤖 Auto-Processor: Starting for user', user.id);

    const processRunningJobs = async () => {
      // Prevent overlapping processing
      if (isProcessingRef.current) {
        console.log('🤖 Auto-Processor: Already processing, skipping...');
        return;
      }

      try {
        // Check if there are any running jobs
        const { data: runningJobs, error: jobsError } = await supabase
          .from('automated_generation_jobs')
          .select('id, job_name, status, images_generated, total_images_target')
          .eq('created_by', user.id)
          .eq('status', 'running')
          .limit(5);

        if (jobsError) {
          console.error('🤖 Auto-Processor: Error checking jobs:', jobsError);
          return;
        }

        if (!runningJobs || runningJobs.length === 0) {
          // No running jobs, skip processing
          return;
        }

        // Check if any jobs still need images
        const jobsNeedingWork = runningJobs.filter(
          j => j.images_generated < j.total_images_target
        );

        if (jobsNeedingWork.length === 0) {
          console.log('🤖 Auto-Processor: All running jobs are complete');
          return;
        }

        console.log(`🤖 Auto-Processor: Found ${jobsNeedingWork.length} jobs needing processing`);
        isProcessingRef.current = true;

        // Trigger the processing edge function
        const { data, error } = await supabase.functions.invoke('process-automation-job', {
          body: {}
        });

        if (error) {
          console.error('🤖 Auto-Processor: Error invoking function:', error);
        } else if (data?.results?.length > 0) {
          const totalGenerated = data.results.reduce(
            (sum: number, r: any) => sum + (r.imagesGenerated || 0), 
            0
          );
          console.log(`🤖 Auto-Processor: Generated ${totalGenerated} images`);
        }
      } catch (error) {
        console.error('🤖 Auto-Processor: Error:', error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Initial check after 5 seconds
    const initialTimeout = setTimeout(processRunningJobs, 5000);

    // Then process every 60 seconds
    intervalRef.current = window.setInterval(processRunningJobs, 60000);

    return () => {
      console.log('🤖 Auto-Processor: Cleaning up...');
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user?.id]);

  return null;
};
